// SEO utilities using react-helmet for meta management
import React from 'react';
import { Helmet } from 'react-helmet';

// Title template for consistent site branding (currently unused but kept for future use)
// eslint-disable-next-line no-unused-vars
const titleTemplate = '%s · SalaryTools';

/**
 * Creates React Helmet meta tags for SEO optimisation
 * @param {object} options - SEO configuration
 * @param {string} options.title - Page title
 * @param {string} [options.description] - Meta description
 * @param {string} [options.canonical] - Canonical URL
 * @param {boolean} [options.noindex=false] - Whether to set noindex
 * @param {array} [options.jsonLd=[]] - Array of JSON-LD objects
 * @returns {React.ReactElement} Helmet component with meta tags
 */
export const makeMeta = ({ 
  title, 
  description, 
  canonical, 
  noindex = false, 
  jsonLd = [] 
}) => {
  // Apply title template manually for consistency
  const formattedTitle = title.includes(' · ') ? title : `${title} · SalaryTools`;
  
  return (
    <Helmet>
      <title>{formattedTitle}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {description && <meta name="description" content={description} />}
      
      {canonical && <link rel="canonical" href={canonical} />}
      
      {noindex && <meta name="robots" content="noindex" />}
      
      {/* Open Graph meta tags */}
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      
      {/* Twitter Card meta tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      
      {/* JSON-LD structured data */}
      {jsonLd.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Creates WebSite structured data for JSON-LD
 * @param {object} options - Website data
 * @param {string} options.siteName - Name of the website
 * @param {string} options.url - Website URL
 * @returns {object} WebSite JSON-LD object
 */
export const makeWebsiteJsonLd = ({ siteName, url }) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: url
  };
};

/**
 * Creates Organization structured data for JSON-LD
 * @param {object} options - Organization data
 * @param {string} options.name - Organization name
 * @param {string} options.url - Organization URL
 * @param {string} options.logoUrl - Logo URL
 * @returns {object} Organization JSON-LD object
 */
export const makeOrganizationJsonLd = ({ name, url, logoUrl }) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: name,
    url: url,
    logo: logoUrl
  };
};

/**
 * Creates BreadcrumbList structured data for JSON-LD
 * @param {array} crumbs - Array of breadcrumb objects
 * @param {string} crumbs[].name - Breadcrumb name
 * @param {string} crumbs[].url - Breadcrumb URL
 * @returns {object} BreadcrumbList JSON-LD object
 */
export const makeBreadcrumbJsonLd = (crumbs) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
};

/**
 * Creates WebApplication structured data for calculator pages
 * @param {object} options - Application data
 * @param {string} options.name - Application name
 * @param {string} options.url - Application URL
 * @param {string} [options.description] - Application description
 * @param {string} [options.operatingSystem='Web'] - Operating system
 * @param {string} [options.applicationCategory='FinanceApplication'] - Application category
 * @returns {object} WebApplication JSON-LD object
 */
export const makeWebApplicationJsonLd = ({ 
  name, 
  url, 
  description, 
  operatingSystem = 'Web',
  applicationCategory = 'FinanceApplication' 
}) => {
  const app = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: name,
    url: url,
    operatingSystem: operatingSystem,
    applicationCategory: applicationCategory
  };
  
  if (description) {
    app.description = description;
  }
  
  return app;
};

/**
 * Creates SoftwareApplication structured data for calculator tools
 * @param {object} options - Software application data
 * @param {string} options.name - Application name
 * @param {string} options.url - Application URL
 * @param {string} [options.description] - Application description
 * @param {string} [options.operatingSystem='Web'] - Operating system
 * @param {string} [options.applicationCategory='FinanceApplication'] - Application category
 * @returns {object} SoftwareApplication JSON-LD object
 */
export const makeSoftwareApplicationJsonLd = ({ 
  name, 
  url, 
  description, 
  operatingSystem = 'Web',
  applicationCategory = 'FinanceApplication' 
}) => {
  const app = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: name,
    url: url,
    operatingSystem: operatingSystem,
    applicationCategory: applicationCategory
  };
  
  if (description) {
    app.description = description;
  }
  
  return app;
};

/**
 * Formats salary amount for SEO-optimized display
 * @param {number} salary - Salary amount
 * @returns {string} Formatted salary string
 */
export const formatSalaryForSEO = (salary) => 
  new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP',
    maximumFractionDigits: 0 
  }).format(salary);

/**
 * Generates SEO-optimized title for salary-specific pages
 * @param {number} salary - Salary amount
 * @param {number} [takeHome] - Take-home pay amount
 * @returns {string} SEO title
 */
export const generateSalaryTitle = (salary, takeHome) => {
  const formattedSalary = formatSalaryForSEO(salary).replace('£', '');
  if (takeHome) {
    const formattedTakeHome = formatSalaryForSEO(takeHome).replace('£', '');
    return `£${formattedSalary} After Tax UK - £${formattedTakeHome} Take Home 2025`;
  }
  return `£${formattedSalary} After Tax UK - Tax Breakdown 2025`;
};

/**
 * Generates SEO-optimized meta description for salary pages
 * @param {number} salary - Salary amount
 * @param {number} [takeHome] - Take-home pay amount
 * @returns {string} Meta description
 */
export const generateSalaryDescription = (salary, takeHome) => {
  const formattedSalary = formatSalaryForSEO(salary);
  if (takeHome) {
    const formattedTakeHome = formatSalaryForSEO(takeHome);
    return `Calculate ${formattedSalary} UK salary breakdown. Take-home pay: ${formattedTakeHome}. Includes tax, National Insurance, pension deductions. Updated for 2025 tax year.`;
  }
  return `Calculate ${formattedSalary} UK salary breakdown. See tax, National Insurance, pension deductions with visual breakdown. Updated for 2025 tax year.`;
};